import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import axios from "axios";
import toast from "react-hot-toast";
import {
  RestaurantReportsState,
  AllRestaurantReportsAPIResponse,
  IndividualRestaurantReportAPIResponse,
} from "../../types/restaurantReports";

// Get token from sessionStorage for authentication
const getAuthToken = () => {
  const auth: any = sessionStorage.getItem("authState");
  const jsonData = JSON.parse(auth || "{}");
  return jsonData?.token;
};

// Initial state
const initialState: RestaurantReportsState = {
  // All restaurants report
  allRestaurantsReport: null,
  allRestaurantsLoading: false,
  allRestaurantsError: null,
  
  // Individual restaurant report
  individualReport: null,
  individualReportLoading: false,
  individualReportError: null,
  
  // Current filters
  selectedTimeRange: "last_30_days",
  selectedRestaurant: "all",
};

// Async thunks

// Temporary mock data function to simulate API response
const createMockRestaurantReportsData = (timeRange: string) => {
  const mockData: AllRestaurantReportsAPIResponse = {
    success: true,
    message: "Restaurant reports retrieved successfully",
    data: {
      reportPeriod: {
        startDate: new Date(Date.now() - (30 * 24 * 60 * 60 * 1000)).toISOString(), // 30 days ago
        endDate: new Date().toISOString()
      },
      combinedSummary: {
        totalOrders: 2189, // Sum of all restaurant orders: 245+189+312+156+198+276+167+223+289+134
        totalRevenue: 585800, // Sum of all restaurant revenues
        totalRestaurants: 10,
        averageDailyOrders: Math.round(2189 / 30)
      },
      combinedMetrics: {
        dailyOrderVolume: {
          "2024-01-20": 113, // Sum: 12+8+15+6+9+14+7+11+16+5
          "2024-01-21": 119, // Sum: 8+12+18+9+11+16+10+13+14+8
          "2024-01-22": 125, // Sum: 15+10+22+8+13+11+9+12+18+7
          "2024-01-23": 104, // Sum: 9+15+12+11+8+13+12+10+15+9
          "2024-01-24": 85,
          "2024-01-25": 91,
          "2024-01-26": 67
        },
        weeklyRevenue: {
          "Week 1": 146500, // Sum of all restaurants' Week 1 revenue
          "Week 2": 160500, // Sum of all restaurants' Week 2 revenue
          "Week 3": 149000, // Sum of all restaurants' Week 3 revenue
          "Week 4": 150400  // Sum of all restaurants' Week 4 revenue
        },
        categoryBreakdown: {
          "Nigerian Cuisine": 1080, // Sum: 120+95+180+45+110+145+65+105+160+55
          "Continental": 623,       // Sum: 85+60+85+70+55+88+42+85+95+48
          "Fast Food": 363,         // Sum: 40+34+47+41+33+43+60+33+34+31
          "Pastries": 123           // Additional category for variety
        },
        monthlyRevenue: {
          "January": 585800, // Total revenue from all restaurants
          "February": 0, // Current month incomplete
          "March": 0,
          "April": 0
        }
      },
      restaurantReports: [
        {
          restaurantId: "rest_001",
          restaurantName: "Tasty Delights Restaurant",
          totalOrders: 245,
          totalRevenue: 68500,
          dailyOrderVolume: {
            "2024-01-20": 12,
            "2024-01-21": 8,
            "2024-01-22": 15,
            "2024-01-23": 9
          },
          weeklyRevenue: {
            "Week 1": 15000,
            "Week 2": 18000,
            "Week 3": 16500,
            "Week 4": 19000
          },
          categoryBreakdown: {
            "Nigerian Cuisine": 120,
            "Continental": 85,
            "Fast Food": 40
          },
          monthlyRevenue: {
            "January": 68500
          }
        },
        {
          restaurantId: "rest_002",
          restaurantName: "Golden Spoon Kitchen",
          totalOrders: 189,
          totalRevenue: 52100,
          dailyOrderVolume: {
            "2024-01-20": 8,
            "2024-01-21": 12,
            "2024-01-22": 10,
            "2024-01-23": 15
          },
          weeklyRevenue: {
            "Week 1": 12000,
            "Week 2": 14000,
            "Week 3": 13000,
            "Week 4": 13100
          },
          categoryBreakdown: {
            "Nigerian Cuisine": 95,
            "Continental": 60,
            "Fast Food": 34
          },
          monthlyRevenue: {
            "January": 52100
          }
        },
        {
          restaurantId: "rest_003",
          restaurantName: "Mama's Kitchen",
          totalOrders: 312,
          totalRevenue: 78900,
          dailyOrderVolume: {
            "2024-01-20": 15,
            "2024-01-21": 18,
            "2024-01-22": 22,
            "2024-01-23": 12
          },
          weeklyRevenue: {
            "Week 1": 18000,
            "Week 2": 20000,
            "Week 3": 18500,
            "Week 4": 22400
          },
          categoryBreakdown: {
            "Nigerian Cuisine": 180,
            "Continental": 85,
            "Fast Food": 47
          },
          monthlyRevenue: {
            "January": 78900
          }
        },
        {
          restaurantId: "rest_004",
          restaurantName: "Urban Eats",
          totalOrders: 156,
          totalRevenue: 42300,
          dailyOrderVolume: {
            "2024-01-20": 6,
            "2024-01-21": 9,
            "2024-01-22": 8,
            "2024-01-23": 11
          },
          weeklyRevenue: {
            "Week 1": 10000,
            "Week 2": 11000,
            "Week 3": 10500,
            "Week 4": 10800
          },
          categoryBreakdown: {
            "Nigerian Cuisine": 45,
            "Continental": 70,
            "Fast Food": 41
          },
          monthlyRevenue: {
            "January": 42300
          }
        },
        {
          restaurantId: "rest_005",
          restaurantName: "Spice Garden Restaurant",
          totalOrders: 198,
          totalRevenue: 59400,
          dailyOrderVolume: {
            "2024-01-20": 9,
            "2024-01-21": 11,
            "2024-01-22": 13,
            "2024-01-23": 8
          },
          weeklyRevenue: {
            "Week 1": 14000,
            "Week 2": 16000,
            "Week 3": 15500,
            "Week 4": 13900
          },
          categoryBreakdown: {
            "Nigerian Cuisine": 110,
            "Continental": 55,
            "Fast Food": 33
          },
          monthlyRevenue: {
            "January": 59400
          }
        },
        {
          restaurantId: "rest_006",
          restaurantName: "The Food Hub",
          totalOrders: 276,
          totalRevenue: 72400,
          dailyOrderVolume: {
            "2024-01-20": 14,
            "2024-01-21": 16,
            "2024-01-22": 11,
            "2024-01-23": 13
          },
          weeklyRevenue: {
            "Week 1": 17000,
            "Week 2": 19000,
            "Week 3": 18500,
            "Week 4": 17900
          },
          categoryBreakdown: {
            "Nigerian Cuisine": 145,
            "Continental": 88,
            "Fast Food": 43
          },
          monthlyRevenue: {
            "January": 72400
          }
        },
        {
          restaurantId: "rest_007",
          restaurantName: "Crispy Chicken Palace",
          totalOrders: 167,
          totalRevenue: 45900,
          dailyOrderVolume: {
            "2024-01-20": 7,
            "2024-01-21": 10,
            "2024-01-22": 9,
            "2024-01-23": 12
          },
          weeklyRevenue: {
            "Week 1": 11000,
            "Week 2": 12500,
            "Week 3": 11200,
            "Week 4": 11200
          },
          categoryBreakdown: {
            "Nigerian Cuisine": 65,
            "Continental": 42,
            "Fast Food": 60
          },
          monthlyRevenue: {
            "January": 45900
          }
        },
        {
          restaurantId: "rest_008",
          restaurantName: "Ocean View Dining",
          totalOrders: 223,
          totalRevenue: 67800,
          dailyOrderVolume: {
            "2024-01-20": 11,
            "2024-01-21": 13,
            "2024-01-22": 12,
            "2024-01-23": 10
          },
          weeklyRevenue: {
            "Week 1": 16500,
            "Week 2": 17800,
            "Week 3": 16800,
            "Week 4": 16700
          },
          categoryBreakdown: {
            "Nigerian Cuisine": 105,
            "Continental": 85,
            "Fast Food": 33
          },
          monthlyRevenue: {
            "January": 67800
          }
        },
        {
          restaurantId: "rest_009",
          restaurantName: "Royal Feast Restaurant",
          totalOrders: 289,
          totalRevenue: 84200,
          dailyOrderVolume: {
            "2024-01-20": 16,
            "2024-01-21": 14,
            "2024-01-22": 18,
            "2024-01-23": 15
          },
          weeklyRevenue: {
            "Week 1": 20000,
            "Week 2": 22000,
            "Week 3": 21200,
            "Week 4": 21000
          },
          categoryBreakdown: {
            "Nigerian Cuisine": 160,
            "Continental": 95,
            "Fast Food": 34
          },
          monthlyRevenue: {
            "January": 84200
          }
        },
        {
          restaurantId: "rest_010",
          restaurantName: "Sunset Grill & Bar",
          totalOrders: 134,
          totalRevenue: 38700,
          dailyOrderVolume: {
            "2024-01-20": 5,
            "2024-01-21": 8,
            "2024-01-22": 7,
            "2024-01-23": 9
          },
          weeklyRevenue: {
            "Week 1": 9500,
            "Week 2": 10200,
            "Week 3": 9800,
            "Week 4": 9200
          },
          categoryBreakdown: {
            "Nigerian Cuisine": 55,
            "Continental": 48,
            "Fast Food": 31
          },
          monthlyRevenue: {
            "January": 38700
          }
        }
      ]
    }
  };
  return mockData;
};

// Get all restaurants reports
export const getAllRestaurantReports = createAsyncThunk<
  AllRestaurantReportsAPIResponse,
  { timeRange?: string } | undefined,
  { rejectValue: string }
>(
  "restaurantReports/getAllRestaurantReports",
  async (params = {}, { rejectWithValue }) => {
    try {
      const token = getAuthToken();
      if (!token) {
        return rejectWithValue("Authentication token not found");
      }

      // TODO: Replace this with actual API call when endpoints are ready
      // For now, return mock data to allow development to continue
      console.log('🚧 Using mock data for restaurant reports - Backend endpoints not ready yet');
      
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      const mockData = createMockRestaurantReportsData(params.timeRange || "last_30_days");
      return mockData;
      
      /* TODO: Uncomment when backend is ready
      // Build query parameters if needed in the future
      const queryParams = new URLSearchParams();
      if (params.timeRange) {
        queryParams.append("timeRange", params.timeRange);
      }

      const queryString = queryParams.toString();
      const url = `${process.env.REACT_APP_RESTAURANT_URL}/reports${
        queryString ? `?${queryString}` : ""
      }`;

      const response = await axios.get<AllRestaurantReportsAPIResponse>(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      return response.data;
      */
    } catch (error: any) {
      const errorMsg =
        error.response?.data?.message ||
        error.message ||
        "Failed to fetch restaurant reports";
      return rejectWithValue(errorMsg);
    }
  }
);

// Temporary mock data function for individual restaurant report
const createMockIndividualRestaurantReport = (restaurantCode: string, timeRange: string) => {
  const mockData: IndividualRestaurantReportAPIResponse = {
    success: true,
    message: "Individual restaurant report retrieved successfully",
    data: {
      restaurantId: "rest_001",
      restaurantName: "Tasty Delights Restaurant",
      reportPeriod: {
        startDate: new Date(Date.now() - (30 * 24 * 60 * 60 * 1000)).toISOString(),
        endDate: new Date().toISOString()
      },
      summary: {
        totalOrders: 245,
        totalRevenue: 68500,
        averageDailyOrders: Math.round(245 / 30)
      },
      dailyOrderVolume: {
        "2024-01-20": 12,
        "2024-01-21": 8,
        "2024-01-22": 15,
        "2024-01-23": 9,
        "2024-01-24": 18,
        "2024-01-25": 22,
        "2024-01-26": 14
      },
      weeklyRevenue: {
        "Week 1": 15000,
        "Week 2": 18000,
        "Week 3": 16500,
        "Week 4": 19000
      },
      categoryBreakdown: {
        "Nigerian Cuisine": 120,
        "Continental": 85,
        "Fast Food": 40
      },
      monthlyRevenue: {
        "January": 68500,
        "February": 0,
        "March": 0,
        "April": 0
      }
    }
  };
  return mockData;
};

// Get individual restaurant report
export const getIndividualRestaurantReport = createAsyncThunk<
  IndividualRestaurantReportAPIResponse,
  { restaurantCode: string; timeRange?: string },
  { rejectValue: string }
>(
  "restaurantReports/getIndividualRestaurantReport",
  async ({ restaurantCode, timeRange }, { rejectWithValue }) => {
    try {
      const token = getAuthToken();
      if (!token) {
        return rejectWithValue("Authentication token not found");
      }

      // TODO: Replace this with actual API call when endpoints are ready
      console.log('🚧 Using mock data for individual restaurant report - Backend endpoints not ready yet');
      
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 800));
      
      const mockData = createMockIndividualRestaurantReport(restaurantCode, timeRange || "last_30_days");
      return mockData;

      /* TODO: Uncomment when backend is ready
      // Build query parameters if needed in the future
      const queryParams = new URLSearchParams();
      if (timeRange) {
        queryParams.append("timeRange", timeRange);
      }

      const queryString = queryParams.toString();
      const url = `${process.env.REACT_APP_RESTAURANT_URL}/reports/${restaurantCode}${
        queryString ? `?${queryString}` : ""
      }`;

      const response = await axios.get<IndividualRestaurantReportAPIResponse>(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      return response.data;
      */
    } catch (error: any) {
      const errorMsg =
        error.response?.data?.message ||
        error.message ||
        "Failed to fetch individual restaurant report";
      return rejectWithValue(errorMsg);
    }
  }
);

// Redux slice
const restaurantReportsSlice = createSlice({
  name: "restaurantReports",
  initialState,
  reducers: {
    // Filter actions
    setTimeRange: (state, action: PayloadAction<string>) => {
      state.selectedTimeRange = action.payload;
    },
    setSelectedRestaurant: (state, action: PayloadAction<string>) => {
      state.selectedRestaurant = action.payload;
    },
    
    // Clear errors
    clearErrors: (state) => {
      state.allRestaurantsError = null;
      state.individualReportError = null;
    },
    
    // Clear individual report when switching to all restaurants view
    clearIndividualReport: (state) => {
      state.individualReport = null;
      state.individualReportError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Get all restaurant reports
      .addCase(getAllRestaurantReports.pending, (state) => {
        state.allRestaurantsLoading = true;
        state.allRestaurantsError = null;
      })
      .addCase(getAllRestaurantReports.fulfilled, (state, action) => {
        state.allRestaurantsLoading = false;
        state.allRestaurantsReport = action.payload.data;
        state.allRestaurantsError = null;
      })
      .addCase(getAllRestaurantReports.rejected, (state, action) => {
        state.allRestaurantsLoading = false;
        state.allRestaurantsError = action.payload || "Failed to fetch restaurant reports";
        toast.error(action.payload || "Failed to fetch restaurant reports");
      })
      
      // Get individual restaurant report
      .addCase(getIndividualRestaurantReport.pending, (state) => {
        state.individualReportLoading = true;
        state.individualReportError = null;
      })
      .addCase(getIndividualRestaurantReport.fulfilled, (state, action) => {
        state.individualReportLoading = false;
        state.individualReport = action.payload.data;
        state.individualReportError = null;
      })
      .addCase(getIndividualRestaurantReport.rejected, (state, action) => {
        state.individualReportLoading = false;
        state.individualReportError = action.payload || "Failed to fetch individual restaurant report";
        toast.error(action.payload || "Failed to fetch individual restaurant report");
      });
  },
});

export const {
  setTimeRange,
  setSelectedRestaurant,
  clearErrors,
  clearIndividualReport,
} = restaurantReportsSlice.actions;

export default restaurantReportsSlice.reducer;